import { supabase } from '../lib/supabase'

/**
 * The Ask box's one call — `ask-dispatches`, a Supabase Edge Function.
 *
 * WHY A FUNCTION AND NOT THE BROWSER. Three reasons, in order of how badly
 * each would go wrong:
 *
 *   1. The Anthropic key. It lives in Edge Function secrets and is never sent
 *      to a browser. A key in client code is a key on the internet.
 *   2. The mailboxes. Gmail is read by a Google service account acting as
 *      tom@ or zeena@, through a domain-wide delegation grant. Nothing about
 *      that can be exposed to a page.
 *   3. The gate. The function calls `is_portal_staff()` with the CALLER's own
 *      session token before it reads a single message, so the studio's
 *      correspondence is behind the same door as everything else here. Called
 *      with an anonymous key it returns 403.
 *
 * It is slow on purpose — twenty to thirty seconds — because it reads whole
 * threads rather than skimming subject lines. See the note in the component
 * about why the wait has to be visible.
 */

export interface AskSource {
  n: number
  mailbox: string
  subject: string
  messages: number
  url: string
}

export interface AskResult {
  answer: string
  /** The model ran out of room mid-sentence. Never hide this. */
  truncated: boolean
  sources: AskSource[]
  threadsRead: number
}

/** Pull the real complaint out of a non-2xx, which arrives as an opaque error. */
async function reasonFrom(error: unknown): Promise<string> {
  const context = (error as { context?: Response })?.context
  if (context && typeof context.json === 'function') {
    try {
      const body = await context.json()
      if (body?.error) return String(body.error)
    } catch {
      // Fall through to the generic message.
    }
  }
  return error instanceof Error ? error.message : String(error)
}

export async function askDispatches(question: string): Promise<AskResult> {
  if (!supabase) throw new Error('Not connected to Portal OS.')

  const { data, error } = await supabase.functions.invoke('ask-dispatches', {
    body: { question },
  })

  if (error) throw new Error(await reasonFrom(error))
  if (data?.error) throw new Error(String(data.error))

  return {
    answer: String(data?.answer ?? ''),
    truncated: Boolean(data?.truncated),
    sources: ((data?.sources ?? []) as Record<string, unknown>[]).map((source) => ({
      n: Number(source.n ?? 0),
      mailbox: String(source.mailbox ?? ''),
      subject: String(source.subject ?? ''),
      messages: Number(source.messages ?? 0),
      url: String(source.url ?? ''),
    })),
    threadsRead: Number(data?.threadsRead ?? 0),
  }
}
